/**
 * Tenant Service
 *
 * Handles tenant-related API calls:
 * - Signup (create new tenant)
 * - Check slug availability
 * - Check subdomain availability
 * - Get current tenant information
 * - WhatsApp configuration
 */

import { createApiAxiosInstance } from '../utils/axiosConfig';

const tenantAxios = createApiAxiosInstance();

export interface SignupTenantRequest {
  cnpj?: string; // Optional: CNPJ of the accounting firm
  name: string;
  contactName: string;
  contactEmail: string;
  contactPassword: string;
  plan: number; // TenantPlan enum: 0=Starter, 1=Professional, 2=Enterprise, 3=Custom
  subdomain?: string; // Optional: auto-generated from name if not provided
  sessionId?: string; // Optional: SignalR session ID for progress tracking
}

export interface SignupTenantResponse {
  id: string;
  name: string;
  slug: string;
  subdomain: string;
  plan: string;
  trialEndsAt: string;
  d1DatabaseId: string;
  r2BucketName: string;
  accessToken: string; // JWT token for immediate login
  sessionId?: string; // SignalR session ID
}

export interface SlugAvailabilityResponse {
  slug: string;
  isAvailable: boolean;
  suggestedSlug: string;
}

export interface SubdomainAvailabilityResponse {
  subdomain: string;
  isAvailable: boolean;
  fullUrl: string;
}

export interface TenantInfoResponse {
  id: string;
  name: string;
  slug: string;
  subdomain: string | null;
  plan: string;
  status: string;
  trialEndsAt: string | null;
  maxCompanies: number;
  maxUsers: number;
  maxStorageGb: number;
  currentCompaniesCount: number;
  currentUsersCount: number;
  currentStorageGb: number;
  contactName: string;
  contactEmail: string;
  logoUrl: string | null;
}

export interface ConfigureWhatsAppRequest {
  accessToken: string;
  businessAccountId: string;
  phoneNumberId: string;
  verifyToken: string;
  webhookSecret: string;
}

/**
 * Sign up a new tenant (accounting firm)
 * Creates dedicated D1 database and R2 bucket with 14-day trial
 * PUBLIC endpoint - no authentication required
 */
export const signupTenant = async (data: SignupTenantRequest): Promise<any> => {
  return await tenantAxios.post('/tenants/signup', data);
};

/**
 * Check if a slug is available
 * PUBLIC endpoint - no authentication required
 */
export const checkSlugAvailability = async (slug: string): Promise<any> => {
  return await tenantAxios.get(`/tenants/check-slug/${slug}`);
};

/**
 * Check if a subdomain is available
 * PUBLIC endpoint - no authentication required
 */
export const checkSubdomainAvailability = async (subdomain: string): Promise<any> => {
  return await tenantAxios.get(`/tenants/check-subdomain/${subdomain}`);
};

/**
 * Get current tenant information
 * Requires authentication - uses JWT token from session
 */
export const getCurrentTenant = async (): Promise<any> => {
  return await tenantAxios.get('/tenants/me');
};

/**
 * Configure WhatsApp Business integration for current tenant
 * Requires authentication
 */
export const configureWhatsApp = async (data: ConfigureWhatsAppRequest): Promise<any> => {
  return await tenantAxios.put('/tenants/settings/whatsapp', data);
};

/**
 * Disable WhatsApp integration for current tenant
 * Requires authentication
 */
export const disableWhatsApp = async (): Promise<any> => {
  return await tenantAxios.delete('/tenants/settings/whatsapp');
};

/**
 * Lookup company data by CNPJ from Receita Federal
 * PUBLIC endpoint - no authentication required
 */
export const lookupCnpj = async (cnpj: string): Promise<any> => {
  const cnpjDigits = cnpj.replace(/\D/g, '');
  return await tenantAxios.get(`/tenants/lookup-cnpj/${cnpjDigits}`);
};

const tenantService = {
  signupTenant,
  checkSlugAvailability,
  checkSubdomainAvailability,
  getCurrentTenant,
  configureWhatsApp,
  disableWhatsApp,
  lookupCnpj,
};

export default tenantService;
